<template>
  <Breadcrumb :items="breadcrumbs" />

  <div class="ds-page">
    <div class="ds-container ds-container--wide">
      <div class="ds-page-header">
        <div>
          <h1 class="ds-page-header__title">طلبات الدفع</h1>
          <p class="ds-page-header__subtitle">مراجعة إيصالات الطلاب والموافقة على الاشتراكات أو رفضها</p>
        </div>
      </div>

      <!-- Filters -->
      <div class="filter">
        <div class="filter__header">
          <div class="filter__header-icon"><i class="pi pi-filter"></i></div>
          <p class="filter__title">عوامل التصفية</p>
        </div>
        <div class="ds-field-grid">
          <div class="form-field">
            <label class="field-label">الحالة</label>
            <select class="form-select" v-model="store.filters.status" @change="reload">
              <option value="">الكل</option>
              <option value="pending">قيد المراجعة</option>
              <option value="approved">مقبول</option>
              <option value="rejected">مرفوض</option>
            </select>
          </div>
          <div class="form-field">
            <label class="field-label">بحث بالطالب (الاسم أو الهاتف)</label>
            <div class="d-flex gap-2">
              <input type="text" class="form-control" v-model="search" placeholder="اكتب اسم أو رقم الطالب..." />
              <button class="ds-btn ds-btn--secondary" style="padding:0.5625rem 0.875rem" v-tooltip.top="'إعادة تعيين'" @click="resetFilters">
                <i class="pi pi-refresh"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <Loading v-if="store.loading" />

      <template v-else>
        <!-- Empty state -->
        <div v-if="!filteredRequests.length" class="empty-state empty-state--card">
          <div class="empty-state__icon"><i class="pi pi-inbox"></i></div>
          <h3 class="empty-state__title">لا توجد طلبات دفع</h3>
          <p class="empty-state__desc">لا توجد طلبات دفع مطابقة لعوامل التصفية الحالية.</p>
        </div>

        <template v-else>
          <!-- Desktop / tablet table -->
          <div class="ds-table-wrap table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th class="ps-3">الطالب</th>
                  <th>الخطة</th>
                  <th class="text-center">المبلغ</th>
                  <th>رقم العملية</th>
                  <th>طريقة الدفع</th>
                  <th class="text-center">الإيصال</th>
                  <th class="text-center">الحالة</th>
                  <th>التاريخ</th>
                  <th class="text-center pe-3">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in filteredRequests" :key="r.id">
                  <td class="ps-3">
                    <div class="fw-bold">{{ r.student?.name || '—' }}</div>
                    <small class="text-muted">{{ r.student?.phone }}</small>
                  </td>
                  <td>{{ planLabel(r.subscription?.plan_type) }}</td>
                  <td class="text-center"><PriceBadge :value="r.amount" /></td>
                  <td class="pm-number">{{ r.transaction_number || '—' }}</td>
                  <td>{{ r.payment_method?.name || '—' }}</td>
                  <td class="text-center">
                    <button v-if="r.receipt_image_link" class="icon-action-btn icon-action-btn--neutral" v-tooltip.top="'عرض الإيصال'" @click="viewImage(r.receipt_image_link)">
                      <i class="pi pi-image"></i>
                    </button>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td class="text-center"><span class="status-badge" :class="reqTone(r.status)">{{ reqLabel(r.status) }}</span></td>
                  <td><small>{{ date(r.created_at) }}</small></td>
                  <td class="text-center pe-3">
                    <div class="icon-action-group justify-content-center">
                      <button class="icon-action-btn icon-action-btn--neutral" v-tooltip.top="'تفاصيل'" @click="openDetails(r)">
                        <i class="pi pi-eye"></i>
                      </button>
                      <template v-if="r.status === 'pending'">
                        <button class="icon-action-btn icon-action-btn--success" v-tooltip.top="'قبول'" @click="approve(r)"><i class="pi pi-check"></i></button>
                        <button class="icon-action-btn icon-action-btn--danger" v-tooltip.top="'رفض'" @click="reject(r)"><i class="pi pi-times"></i></button>
                      </template>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile card list -->
          <div class="mobile-list">
            <div v-for="r in filteredRequests" :key="r.id" class="mobile-list-card">
              <div class="mobile-list-card__top">
                <div class="mobile-list-card__meta">
                  <div class="mobile-list-card__title">{{ r.student?.name || '—' }}</div>
                  <small class="text-muted">{{ r.student?.phone }}</small>
                </div>
                <span class="status-badge" :class="reqTone(r.status)">{{ reqLabel(r.status) }}</span>
              </div>
              <div class="mobile-list-card__facts">
                <span>الخطة: <b>{{ planLabel(r.subscription?.plan_type) }}</b></span>
                <span>المبلغ: <b><PriceBadge :value="r.amount" /></b></span>
                <span v-if="r.payment_method?.name">الطريقة: <b>{{ r.payment_method.name }}</b></span>
                <span>التاريخ: <b>{{ date(r.created_at) }}</b></span>
              </div>
              <div class="mobile-list-card__actions">
                <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="openDetails(r)"><i class="pi pi-eye"></i> تفاصيل</button>
                <template v-if="r.status === 'pending'">
                  <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="approve(r)"><i class="pi pi-check"></i></button>
                  <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="reject(r)"><i class="pi pi-times"></i></button>
                </template>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-center mt-3" v-if="store.pagination && store.pagination.last_page > 1">
            <pagination :data="store.pagination" @pagination-change-page="store.fetchRequests" class="pagination mb-0" />
          </div>
        </template>
      </template>
    </div>
  </div>

  <!-- Details dialog -->
  <Dialog v-model:visible="detailsOpen" modal :style="{ width: '42rem' }" :breakpoints="{ '640px': '95vw' }" header="تفاصيل طلب الدفع">
    <div v-if="detailsLoading" class="text-center py-4"><i class="pi pi-spin pi-spinner"></i></div>
    <div v-else-if="details">
      <div class="detail-grid">
        <div class="detail-row"><span class="detail-row__label">الطالب</span><span class="detail-row__value">{{ details.student?.name }} — {{ details.student?.phone }}</span></div>
        <div class="detail-row"><span class="detail-row__label">المنتج</span><span class="detail-row__value">{{ productLabel(details) }}</span></div>
        <div class="detail-row"><span class="detail-row__label">الخطة</span><span class="detail-row__value">{{ planLabel(details.subscription?.plan_type) }}</span></div>
        <div class="detail-row"><span class="detail-row__label">المبلغ</span><span class="detail-row__value"><PriceBadge :value="details.amount" /></span></div>
        <div class="detail-row"><span class="detail-row__label">رقم العملية</span><span class="detail-row__value">{{ details.transaction_number || '—' }}</span></div>
        <div class="detail-row"><span class="detail-row__label">طريقة الدفع</span><span class="detail-row__value">{{ details.payment_method?.name || '—' }}</span></div>
        <div class="detail-row"><span class="detail-row__label">الحالة</span><span class="detail-row__value"><span class="status-badge" :class="reqTone(details.status)">{{ reqLabel(details.status) }}</span></span></div>
        <div class="detail-row"><span class="detail-row__label">ملاحظات</span><span class="detail-row__value">{{ details.notes || '—' }}</span></div>
        <div class="detail-row" v-if="details.reviewer"><span class="detail-row__label">راجعها</span><span class="detail-row__value">{{ details.reviewer?.name }}</span></div>
      </div>
      <div class="text-center mt-3" v-if="details.receipt_image_link">
        <img :src="details.receipt_image_link" class="img-fluid rounded border" style="max-height:280px; cursor:pointer;" alt="الإيصال" @click="viewImage(details.receipt_image_link)" />
      </div>
    </div>
    <template #footer>
      <template v-if="details && details.status === 'pending'">
        <button class="ds-btn ds-btn--danger" @click="reject(details)"><i class="pi pi-times"></i> رفض</button>
        <button class="ds-btn ds-btn--primary" @click="approve(details)"><i class="pi pi-check"></i> قبول</button>
      </template>
      <button class="btn-cancel" @click="detailsOpen = false">إغلاق</button>
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import pagination from 'laravel-vue-pagination';
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import PriceBadge from '@/views/courses/PriceBadge.vue';
import { usePaymentRequestStore } from '@/stores/usePaymentRequestStore.js';
import { planLabel, requestStatusLabel, requestStatusTone, typeLabel, formatMoney, formatDate, apiError } from '@/composables/subscriptionMeta.js';

export default {
  name: 'PaymentRequestsIndex',
  components: { Dialog, pagination, Breadcrumb, Loading, PriceBadge },
  data() {
    return {
      breadcrumbs: [{ name: 'الرئيسية', path: '/' }, { name: 'طلبات الدفع', path: '/payment-requests' }],
      search: '',
      detailsOpen: false,
      detailsLoading: false,
      details: null,
    };
  },
  computed: {
    store() { return usePaymentRequestStore(); },
    filteredRequests() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.store.requests;
      return this.store.requests.filter(r => {
        const name = (r.student?.name || '').toLowerCase();
        const phone = (r.student?.phone || '').toLowerCase();
        return name.includes(q) || phone.includes(q);
      });
    },
  },
  methods: {
    planLabel,
    reqLabel: requestStatusLabel,
    reqTone: requestStatusTone,
    money: formatMoney,
    date: formatDate,
    productLabel(r) {
      const s = r.subscription?.subscribable;
      const type = typeLabel(r.subscription?.subscribable_type);
      const name = s?.name || s?.title || '';
      return name ? `${name} (${type})` : type || '—';
    },
    reload() { this.store.fetchRequests(1); },
    resetFilters() {
      this.store.filters.status = '';
      this.search = '';
      this.store.fetchRequests(1);
    },
    viewImage(src) {
      Swal.fire({ imageUrl: src, imageAlt: 'الإيصال', showConfirmButton: false, showCloseButton: true, width: 'auto' });
    },
    async openDetails(r) {
      this.detailsOpen = true;
      this.detailsLoading = true;
      this.details = null;
      try {
        this.details = await this.store.show(r.id);
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 2000, showConfirmButton: false });
        this.detailsOpen = false;
      } finally {
        this.detailsLoading = false;
      }
    },
    async approve(r) {
      const { value: note, isConfirmed } = await Swal.fire({
        title: 'قبول الدفع', input: 'textarea', inputLabel: 'ملاحظة (اختياري)',
        inputPlaceholder: 'ملاحظة تُحفظ مع الطلب...', showCancelButton: true,
        confirmButtonText: 'قبول وتفعيل الاشتراك', cancelButtonText: 'إلغاء', confirmButtonColor: '#16a34a',
      });
      if (!isConfirmed) return;
      try {
        await this.store.approve(r.id, note || null);
        this.detailsOpen = false;
        await this.store.fetchRequests(this.store.pagination?.current_page || 1);
        this.store.fetchPendingCount();
        Swal.fire({ icon: 'success', title: 'تم قبول الدفع وتفعيل الاشتراك', timer: 1500, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false });
      }
    },
    async reject(r) {
      const { value: note, isConfirmed } = await Swal.fire({
        title: 'رفض الدفع', input: 'textarea', inputLabel: 'سبب الرفض (اختياري)',
        inputPlaceholder: 'سبب يظهر للطالب...', showCancelButton: true,
        confirmButtonText: 'رفض الطلب', cancelButtonText: 'إلغاء', confirmButtonColor: '#dc3545',
      });
      if (!isConfirmed) return;
      try {
        await this.store.reject(r.id, note || null);
        this.detailsOpen = false;
        await this.store.fetchRequests(this.store.pagination?.current_page || 1);
        this.store.fetchPendingCount();
        Swal.fire({ icon: 'success', title: 'تم رفض الطلب', timer: 1500, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false });
      }
    },
  },
  mounted() {
    this.store.fetchRequests(1);
  },
};
</script>

<style scoped>
.pm-number { font-family: 'Courier New', monospace; color: var(--text-secondary); }
.detail-grid { display: flex; flex-direction: column; }
.detail-row {
  display: flex; align-items: center; gap: 1rem; padding: 0.625rem 0; border-bottom: 1px solid var(--surface-border);
}
.detail-row:last-child { border-bottom: none; }
.detail-row__label { width: 110px; flex-shrink: 0; font-size: 0.8125rem; color: var(--text-secondary); font-weight: 600; }
.detail-row__value { font-size: 0.875rem; color: var(--text-primary); }
</style>
